import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  IconButton,
  Alert,
  Divider,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Container,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText
} from "@mui/material";
import {
  Search,
  Delete,
  LocalPharmacy,
  Person,
  Close,
  CheckCircle,
  Warning
} from "@mui/icons-material";
import {
  medicinesAPI,
  salesAPI,
  pharmacyPatientsAPI,
} from "../../services/API";
import { useSnackbar } from "notistack";
import { calculateAge } from "../../../../../../../utils";

export const NewSale = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [medicines, setMedicines] = useState([]);
  const [patients, setPatients] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paidAmount, setPaidAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stockValidation, setStockValidation] = useState({});
  const [interactionDialog, setInteractionDialog] = useState({
    open: false,
    interactions: [],
  });
  const [batchSelection, setBatchSelection] = useState({
    open: false,
    medicine: null,
    batches: [],
  });

  // Medicine search states
  const [medicineSearchTerm, setMedicineSearchTerm] = useState("");
  const [medicineLoading, setMedicineLoading] = useState(false);
  const [medicineFilters, setMedicineFilters] = useState({
    inStock: true,
  });

  // Patient search states
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [patientLoading, setPatientLoading] = useState(false);

  // Debounced medicine search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (medicineSearchTerm.trim() !== "") {
        fetchMedicines();
      } else {
        setMedicines([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [medicineSearchTerm, medicineFilters]);

  // Debounced patient search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (patientSearchTerm.trim() !== "") {
        fetchPharmacyPatients();
      } else {
        setPatients([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [patientSearchTerm]);

  const fetchMedicines = async () => {
    try {
      setMedicineLoading(true);
      const params = {
        search: medicineSearchTerm,
        populate: "batches",
        stock: medicineFilters.inStock ? "available" : undefined,
        limit: 15,
        page: 1,
      };

      const response = await medicinesAPI.getAll(params);
      setMedicines(response.data.data.medicines || []);
    } catch (error) {
      enqueueSnackbar("Error fetching medicines", { variant: "error" });
    } finally {
      setMedicineLoading(false);
    }
  };

  const fetchPharmacyPatients = async () => {
    try {
      setPatientLoading(true);
      const response = await pharmacyPatientsAPI.getPharmacyPatients({
        name: patientSearchTerm,
      });
      setPatients(response.data.patients || []);
    } catch (error) {
      enqueueSnackbar("Error fetching patients", { variant: "error" });
    } finally {
      setPatientLoading(false);
    }
  };

  const validateCartStock = async () => {
    if (cart.length === 0) {
      setStockValidation({});
      return;
    }

    try {
      const items = cart.map((item) => ({
        medicine: item.medicine._id,
        quantity: item.quantity,
      }));

      const response = await salesAPI.checkAvailability({ items });
      const { availability, interactions } = response.data.data;

      const validation = {};
      availability.forEach((avail, index) => {
        validation[cart[index].medicine._id] = {
          sufficient: avail.sufficient,
          available: avail.available,
          warnings: avail.warnings,
        };
      });

      setStockValidation(validation);

      if (interactions.length > 0) {
        setInteractionDialog({
          open: true,
          interactions: interactions,
        });
      }
    } catch (error) {
      console.error("Stock validation error:", error);
    }
  };

  useEffect(() => {
    validateCartStock();
  }, [cart]);

  const addToCart = (medicine) => {
    const availableBatches =
      medicine.batches?.filter((b) => b.availableQuantity > 0) || [];

    if (availableBatches.length === 0) {
      enqueueSnackbar("No available stock for this medicine", {
        variant: "warning",
      });
      return;
    }

    if (availableBatches.length === 1) {
      addToCartWithBatch(medicine, availableBatches[0]);
    } else {
      setBatchSelection({
        open: true,
        medicine,
        batches: availableBatches,
      });
    }
  };

  const addToCartWithBatch = (medicine, batch) => {
    const existingItem = cart.find(
      (item) =>
        item.medicine._id === medicine._id && item.batchStock === batch._id
    );

    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.medicine._id === medicine._id && item.batchStock === batch._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          medicine,
          quantity: 1,
          sellingPrice: batch.sellingPrice,
          costPrice: batch.costPrice,
          mrp: batch.mrp,
          discount: 0,
          batchStock: batch._id,
          batchNumber: batch.batchNumber,
          expiryDate: batch.expiryDate,
        },
      ]);
    }

    enqueueSnackbar(`${medicine.name} added to cart`, { variant: "success" });
  };

  const removeFromCart = (medicineId) => {
    setCart(cart.filter((item) => item.medicine._id !== medicineId));
  };

  const updateQuantity = (medicineId, quantity) => {
    if (quantity < 1) {
      removeFromCart(medicineId);
      return;
    }

    setCart(
      cart.map((item) =>
        item.medicine._id === medicineId ? { ...item, quantity } : item
      )
    );
  };

  const updateSellingPrice = (medicineId, price) => {
    setCart(
      cart.map((item) =>
        item.medicine._id === medicineId
          ? { ...item, sellingPrice: parseFloat(price) || 0 }
          : item
      )
    );
  };

  const updateDiscount = (medicineId, discount) => {
    setCart(
      cart.map((item) =>
        item.medicine._id === medicineId
          ? {
            ...item,
            discount: Math.min(100, Math.max(0, parseFloat(discount) || 0)),
          }
          : item
      )
    );
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => {
      const itemTotal = item.quantity * item.sellingPrice;
      const itemDiscount = itemTotal * (item.discount / 100);
      return sum + (itemTotal - itemDiscount);
    }, 0);

    const tax = subtotal * 0.18;
    const grandTotal = subtotal + tax;

    const totalCost = cart.reduce(
      (sum, item) => sum + item.costPrice * item.quantity,
      0
    );
    const totalProfit = subtotal - totalCost;

    return { subtotal, tax, grandTotal, totalCost, totalProfit };
  };

  const handleClearMedicineSearch = () => {
    setMedicineSearchTerm("");
    setMedicines([]);
  };

  const handleCheckout = async () => {
    if (!selectedPatient) {
      enqueueSnackbar("Please select a patient first", { variant: "warning" });
      return;
    }

    if (cart.length === 0) {
      enqueueSnackbar("Please add items to cart", { variant: "warning" });
      return;
    }

    const invalidItems = cart.filter(
      (item) => !item.sellingPrice || item.sellingPrice === 0
    );
    if (invalidItems.length > 0) {
      enqueueSnackbar("Please set selling price for all items", {
        variant: "error",
      });
      return;
    }

    const overpricedItems = cart.filter((item) => item.sellingPrice > item.mrp);
    if (overpricedItems.length > 0) {
      enqueueSnackbar("Some items have selling price exceeding MRP", {
        variant: "error",
      });
      return;
    }

    const hasStockIssues = Object.values(stockValidation).some(
      (validation) => !validation.sufficient
    );
    if (hasStockIssues) {
      enqueueSnackbar("Some items have insufficient stock", {
        variant: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const { grandTotal } = calculateTotals();
      const finalPaidAmount = parseFloat(paidAmount) || 0;

      if (finalPaidAmount < 0) {
        throw new Error("Paid amount cannot be negative");
      }

      if (finalPaidAmount > grandTotal * 2) {
        throw new Error("Paid amount seems unusually high. Please verify.");
      }

      const nearExpiryItems = cart.filter((item) => {
        const daysToExpiry = Math.ceil(
          (new Date(item.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        return daysToExpiry <= 7;
      });

      if (nearExpiryItems.length > 0) {
        enqueueSnackbar(
          `${nearExpiryItems.length} items are near expiry. Please inform customer.`,
          { variant: "warning", autoHideDuration: 6000 }
        );
      }

      let paymentStatus = "pending";
      if (finalPaidAmount >= grandTotal) {
        paymentStatus = "paid";
      } else if (finalPaidAmount > 0 && finalPaidAmount < grandTotal) {
        paymentStatus = "partially_paid";
      }

      const saleData = {
        // Patient information
        uhid: selectedPatient.uhid,
        op_no: selectedPatient.op_no,
        ipd_no: selectedPatient.patientType === "IPD" ? selectedPatient.ipd_no : null,
        patientType: selectedPatient.patientType,
        patientId: selectedPatient._id,

        // Sale items
        items: cart.map((item) => ({
          medicine: item.medicine._id,
          batchStock: item.batchStock,
          quantity: item.quantity,
          sellingPrice: item.sellingPrice,
          costPrice: item.costPrice,
          mrp: item.mrp,
          discount: item.discount,
          taxRate: 18,
        })),
        discount: 0,
        paymentMethod,
        paidAmount: finalPaidAmount,
        paymentStatus,
        notes: `Sale created on ${new Date().toLocaleDateString()}`,
      };

      const response = await salesAPI.create(saleData);

      enqueueSnackbar("Sale completed successfully!", { variant: "success" });

      const invoice = response.data.data.salesInvoice;
      enqueueSnackbar(
        `Invoice #${invoice.invoiceNumber} created. Status: ${invoice.paymentStatus}`,
        { variant: "info", autoHideDuration: 5000 }
      );

      setCart([]);
      setPaidAmount(0);
      setPaymentMethod("cash");

      setTimeout(() => {
        navigate("/hospital/pharmacy/sales");
      }, 2000);
    } catch (error) {
      let errorMessage = "Error processing sale";

      if (error.response) {
        const backendError = error.response.data;
        errorMessage = backendError.message || errorMessage;

        if (backendError.code === "STOCK_UNAVAILABLE") {
          errorMessage =
            "Insufficient stock. Please update quantities and try again.";
          validateCartStock();
        } else if (backendError.code === "PRICE_VALIDATION_FAILED") {
          errorMessage =
            "Price validation failed. Please check selling prices.";
        } else if (backendError.code === "BATCH_EXPIRED") {
          errorMessage =
            "Selected batch has expired. Please select different batches.";
          validateCartStock();
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message || errorMessage;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 5000,
      });

      console.error("Checkout error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaidAmountChange = (amount) => {
    const paid = parseFloat(amount) || 0;
    setPaidAmount(amount);

    const { grandTotal } = calculateTotals();
    const change = paid - grandTotal;

    if (change >= 0) {
      enqueueSnackbar(`Change: ₹${change.toFixed(2)}`, {
        variant: "info",
        autoHideDuration: 3000,
      });
    } else {
      enqueueSnackbar(`Due: ₹${Math.abs(change).toFixed(2)}`, {
        variant: "warning",
        autoHideDuration: 3000,
      });
    }
  };

  const { subtotal, tax, grandTotal, totalCost, totalProfit } =
    calculateTotals();
  const changeAmount = Math.max(0, (parseFloat(paidAmount) || 0) - grandTotal);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box>
          <Typography variant="h3" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 4 }}>
            New Sale
          </Typography>
          <Grid container spacing={3}>
            {/* Left Column - Patient & Medicine Search */}
            <Grid item xs={12} md={8}>
              {/* Patient Selection */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Patient Information
                  </Typography>
                  
                  <Autocomplete
                    fullWidth
                    options={patients}
                    loading={patientLoading}
                    getOptionLabel={(option) => 
                      `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.uhid
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Patient (UHID / Name / Mobile)"
                        value={patientSearchTerm}
                        onChange={(e) => setPatientSearchTerm(e.target.value)}
                        sx={{ fontSize: '1.1rem' }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { fontSize: '1.1rem' },
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ fontSize: 28 }} />
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <ListItem {...props}>
                        <ListItemAvatar>
                          <Avatar>
                            <Person />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${option.first_name || ''} ${option.last_name || ''}`.trim() || option.uhid}
                          secondary={
                            <>
                              UHID: {option.uhid} •{" "}
                              {option.patientType === "OPD"
                                ? `OPD: ${option.op_no}`
                                : `IPD: ${option.ipd_no}`}{" "}
                              • Age:{" "}
                              {option.dob ? calculateAge(option.dob) : "—"} •{" "}
                              <Chip
                                label={option.patientType}
                                color={
                                  option.patientType === "OPD"
                                    ? "primary"
                                    : "warning"
                                }
                                size="small"
                                sx={{ fontSize: '0.8rem' }}
                              />
                            </>
                          }
                          primaryTypographyProps={{ sx: { fontSize: '1.1rem' } }}
                          secondaryTypographyProps={{ sx: { fontSize: '1rem' } }}
                        />
                      </ListItem>
                    )}
                    onChange={(_, patient) => setSelectedPatient(patient)}
                  />

                  {selectedPatient && (
                    <Card
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: "#e8f4ff",
                        borderLeft: "6px solid #1976d2",
                        borderRadius: 2,
                        position: "relative",
                      }}
                    >
                      <IconButton
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => setSelectedPatient(null)}
                      >
                        <Close />
                      </IconButton>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                            {selectedPatient.first_name} {selectedPatient.last_name || ""}
                          </Typography>

                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            Age: {selectedPatient.dob ? calculateAge(selectedPatient.dob) : "—"}
                          </Typography>

                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            Gender: {selectedPatient.gender || "—"}
                          </Typography>

                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            Mobile: {selectedPatient.address?.mobile || "—"}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                            <strong>UHID:</strong> {selectedPatient.uhid}
                          </Typography>

                          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                            <strong>OP No:</strong> {selectedPatient.op_no || "-"}
                          </Typography>

                          {selectedPatient.patientType === "IPD" && (
                            <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                              <strong>IPD No:</strong> {selectedPatient.ipd_no}
                            </Typography>
                          )}

                          <Chip
                            label={selectedPatient.patientType}
                            color={selectedPatient.patientType === "IPD" ? "warning" : "primary"}
                            sx={{ mt: 1, fontSize: '1rem' }}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  )}
                </CardContent>
              </Card>

              {/* Medicine Search */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Add Medicines
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search medicines by name, generic name, or brand..."
                    value={medicineSearchTerm}
                    onChange={(e) => setMedicineSearchTerm(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    InputProps={{
                      sx: { fontSize: '1.1rem' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 28 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Quick Filters */}
                  <Grid container spacing={1} sx={{ mb: 2, mt: 2 }} alignItems="center">
                    <Grid item>
                      <Chip
                        label="In Stock Only"
                        color={medicineFilters.inStock ? "primary" : "default"}
                        onClick={() =>
                          setMedicineFilters((prev) => ({
                            ...prev,
                            inStock: !prev.inStock,
                          }))
                        }
                        variant={medicineFilters.inStock ? "filled" : "outlined"}
                        sx={{ fontSize: '1rem' }}
                      />
                    </Grid>
                    {medicineSearchTerm && (
                      <Grid item>
                        <Button
                          size="medium"
                          onClick={handleClearMedicineSearch}
                          sx={{ ml: 1, fontSize: '1rem' }}
                        >
                          Clear Search
                        </Button>
                      </Grid>
                    )}
                  </Grid>

                  {/* Medicine Results */}
                  {medicineSearchTerm && (
                    <Box sx={{ mt: 2, maxHeight: 300, overflow: "auto" }}>
                      {medicineLoading ? (
                        <Box
                          sx={{ display: "flex", justifyContent: "center", py: 2 }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      ) : (
                        <>
                          {medicines.map((medicine) => {
                            const availableBatches =
                              medicine.batches?.filter(
                                (b) => b.availableQuantity > 0
                              ) || [];
                            const totalStock = availableBatches.reduce(
                              (sum, batch) => sum + batch.availableQuantity,
                              0
                            );

                            return (
                              <Box
                                key={medicine._id}
                                sx={{
                                  p: 2,
                                  mb: 1,
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "action.hover" },
                                }}
                                onClick={() => addToCart(medicine)}
                              >
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                  {medicine.name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {medicine.genericName} • {medicine.strength} •{" "}
                                  {medicine.form}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                                  {medicine.brand}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={`Stock: ${totalStock}`}
                                    size="medium"
                                    color={totalStock > 0 ? "success" : "error"}
                                    variant="outlined"
                                    sx={{ fontSize: '0.9rem' }}
                                  />
                                  {availableBatches.length > 1 && (
                                    <Chip
                                      label={`${availableBatches.length} batches`}
                                      size="medium"
                                      variant="outlined"
                                      sx={{ fontSize: '0.9rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                          {medicines.length === 0 && (
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              textAlign="center"
                              py={3}
                              sx={{ fontSize: '1.1rem' }}
                            >
                              No medicines found
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Cart Items */}
              <Card>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Cart Items
                  </Typography>

                  {cart.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                      <LocalPharmacy
                        sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
                      />
                      <Typography variant="h4" color="text.secondary" sx={{ fontSize: '1.3rem' }}>
                        No items in cart. Search and add medicines above.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Price</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Disc.%</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Profit</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Stock</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cart.map((item) => {
                            const validation = stockValidation[item.medicine._id];
                            const itemAmount = item.quantity * item.sellingPrice;
                            const itemDiscount = itemAmount * (item.discount / 100);
                            const itemNetAmount = itemAmount - itemDiscount;
                            const itemProfit =
                              (item.sellingPrice - item.costPrice) * item.quantity;
                            const profitMargin =
                              item.costPrice > 0
                                ? (
                                  ((item.sellingPrice - item.costPrice) /
                                    item.costPrice) *
                                  100
                                ).toFixed(1)
                                : 0;

                            return (
                              <TableRow key={item.medicine._id}>
                                <TableCell sx={{ py: 2 }}>
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      fontWeight="bold"
                                      sx={{ fontSize: '1.1rem' }}
                                    >
                                      {item.medicine.name}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                      sx={{ fontSize: '1rem' }}
                                    >
                                      {item.medicine.genericName}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                      sx={{ fontSize: '1rem' }}
                                    >
                                      Batch: {item.batchNumber} • Exp:{" "}
                                      {new Date(
                                        item.expiryDate
                                      ).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{ fontSize: '1.1rem' }}
                                  >
                                    ₹{item.costPrice}
                                  </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ py: 2 }}>
                                  <TextField
                                    size="medium"
                                    type="number"
                                    value={item.sellingPrice}
                                    onChange={(e) => {
                                      const newPrice =
                                        parseFloat(e.target.value) || 0;
                                      if (newPrice > item.mrp) {
                                        enqueueSnackbar(
                                          `Selling price cannot exceed MRP: ₹${item.mrp}`,
                                          { variant: "warning" }
                                        );
                                        return;
                                      }
                                      if (newPrice < item.costPrice) {
                                        enqueueSnackbar(
                                          `Selling price should be above cost: ₹${item.costPrice}`,
                                          { variant: "warning" }
                                        );
                                        return;
                                      }
                                      updateSellingPrice(
                                        item.medicine._id,
                                        newPrice
                                      );
                                    }}
                                    sx={{ width: 100, fontSize: '1.1rem' }}
                                    inputProps={{
                                      min: item.costPrice,
                                      max: item.mrp,
                                      step: 0.01,
                                      style: { fontSize: '1.1rem' }
                                    }}
                                  />
                                </TableCell>

                                <TableCell align="center" sx={{ py: 2 }}>
                                  <TextField
                                    size="medium"
                                    type="number"
                                    value={item.discount}
                                    onChange={(e) =>
                                      updateDiscount(
                                        item.medicine._id,
                                        e.target.value
                                      )
                                    }
                                    sx={{ width: 90, fontSize: '1.1rem' }}
                                    inputProps={{ 
                                      min: 0, 
                                      max: 100, 
                                      step: 1,
                                      style: { fontSize: '1.1rem' }
                                    }}
                                  />
                                </TableCell>

                                <TableCell align="center" sx={{ py: 2 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <IconButton
                                      size="large"
                                      onClick={() =>
                                        updateQuantity(
                                          item.medicine._id,
                                          item.quantity - 1
                                        )
                                      }
                                      disabled={item.quantity <= 1}
                                      sx={{ fontSize: '1.5rem' }}
                                    >
                                      -
                                    </IconButton>
                                    <Typography
                                      variant="h6"
                                      sx={{ minWidth: 40, textAlign: "center", fontSize: '1.2rem' }}
                                    >
                                      {item.quantity}
                                    </Typography>
                                    <IconButton
                                      size="large"
                                      onClick={() =>
                                        updateQuantity(
                                          item.medicine._id,
                                          item.quantity + 1
                                        )
                                      }
                                      sx={{ fontSize: '1.5rem' }}
                                    >
                                      +
                                    </IconButton>
                                  </Box>
                                </TableCell>

                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                    ₹{itemNetAmount.toFixed(2)}
                                  </Typography>
                                  {item.discount > 0 && (
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                      sx={{ fontSize: '1rem' }}
                                    >
                                      Save: ₹{itemDiscount.toFixed(2)}
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell align="right" sx={{ py: 2 }}>
                                  <Typography
                                    variant="h6"
                                    color={
                                      itemProfit >= 0
                                        ? "success.main"
                                        : "error.main"
                                    }
                                    fontWeight="bold"
                                    sx={{ fontSize: '1.1rem' }}
                                  >
                                    ₹{itemProfit.toFixed(2)}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ fontSize: '1rem' }}
                                  >
                                    {profitMargin}%
                                  </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ py: 2 }}>
                                  {validation ? (
                                    <Chip
                                      icon={
                                        validation.sufficient ? (
                                          <CheckCircle sx={{ fontSize: 20 }} />
                                        ) : (
                                          <Warning sx={{ fontSize: 20 }} />
                                        )
                                      }
                                      label={validation.available}
                                      color={
                                        validation.sufficient ? "success" : "error"
                                      }
                                      size="medium"
                                      variant={
                                        validation.sufficient
                                          ? "filled"
                                          : "outlined"
                                      }
                                      sx={{ fontSize: '1rem' }}
                                    />
                                  ) : (
                                    <CircularProgress size={24} />
                                  )}
                                </TableCell>

                                <TableCell align="center" sx={{ py: 2 }}>
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      removeFromCart(item.medicine._id)
                                    }
                                    size="large"
                                  >
                                    <Delete sx={{ fontSize: 28 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Order Summary */}
            <Grid item xs={12} md={4}>
              <Card sx={{ position: "sticky", top: 100 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Order Summary
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Subtotal:</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                        ₹{subtotal.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Tax (18%):</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{tax.toFixed(2)}</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Total Cost:</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                        ₹{totalCost.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                        Total Profit:
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color={totalProfit >= 0 ? "success.main" : "error.main"}
                        sx={{ fontSize: '1.2rem' }}
                      >
                        ₹{totalProfit.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                        Total:
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="primary.main"
                        sx={{ fontSize: '1.3rem' }}
                      >
                        ₹{grandTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{ mb: 2, fontSize: '1.1rem' }}
                    InputProps={{ sx: { fontSize: '1.1rem' } }}
                    InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                  >
                    <MenuItem value="cash" sx={{ fontSize: '1.1rem' }}>Cash</MenuItem>
                    <MenuItem value="card" sx={{ fontSize: '1.1rem' }}>Card</MenuItem>
                    <MenuItem value="upi" sx={{ fontSize: '1.1rem' }}>UPI</MenuItem>
                    <MenuItem value="insurance" sx={{ fontSize: '1.1rem' }}>Insurance</MenuItem>
                    <MenuItem value="credit" sx={{ fontSize: '1.1rem' }}>Credit</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    type="number"
                    label="Paid Amount"
                    value={paidAmount}
                    onChange={(e) => handlePaidAmountChange(e.target.value)}
                    sx={{ mb: 2, fontSize: '1.1rem' }}
                    inputProps={{ min: 0, step: 0.01, style: { fontSize: '1.1rem' } }}
                    InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                  />

                  {paidAmount > 0 && (
                    <Alert
                      severity={changeAmount >= 0 ? "success" : "warning"}
                      sx={{ 
                        mb: 2,
                        fontSize: '1.1rem',
                        '& .MuiAlert-message': { fontSize: '1.1rem' }
                      }}
                    >
                      {changeAmount >= 0
                        ? `Change: ₹${changeAmount.toFixed(2)}`
                        : `Due: ₹${Math.abs(changeAmount).toFixed(2)}`}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCheckout}
                    disabled={cart.length === 0 || loading || !selectedPatient}
                    sx={{ 
                      py: 2, 
                      fontSize: '1.2rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={28} color="inherit" />
                    ) : (
                      `Complete Sale - ₹${grandTotal.toFixed(2)}`
                    )}
                  </Button>

                  {!selectedPatient && (
                    <Alert severity="warning" sx={{ mt: 2, fontSize: '1.1rem' }}>
                      Please select a patient first
                    </Alert>
                  )}

                  {Object.values(stockValidation).some((v) => !v.sufficient) && (
                    <Alert severity="error" sx={{ 
                      mt: 2,
                      fontSize: '1.1rem',
                      '& .MuiAlert-message': { fontSize: '1.1rem' }
                    }}>
                      Some items have insufficient stock. Please adjust quantities.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Batch Selection Dialog */}
          <Dialog
            open={batchSelection.open}
            onClose={() =>
              setBatchSelection({ open: false, medicine: null, batches: [] })
            }
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: '1.5rem' }}>
              Select Batch for {batchSelection.medicine?.name}
            </DialogTitle>
            <DialogContent>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                      <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry</TableCell>
                      <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Available</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Price</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>MRP</TableCell>
                      <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchSelection.batches.map((batch) => {
                      const daysToExpiry = Math.ceil(
                        (new Date(batch.expiryDate) - new Date()) /
                        (1000 * 60 * 60 * 24)
                      );
                      return (
                        <TableRow key={batch._id} hover>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                              {batch.batchNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Chip
                              label={new Date(
                                batch.expiryDate
                              ).toLocaleDateString()}
                              color={daysToExpiry <= 30 ? "warning" : "default"}
                              size="medium"
                              sx={{ fontSize: '1rem' }}
                            />
                            {daysToExpiry <= 30 && (
                              <Typography
                                variant="body1"
                                display="block"
                                color="warning.main"
                                sx={{ fontSize: '1rem', mt: 0.5 }}
                              >
                                {daysToExpiry} days left
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              {batch.availableQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              ₹{batch.sellingPrice}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{batch.mrp}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Button
                              variant="contained"
                              size="medium"
                              onClick={() => {
                                addToCartWithBatch(batchSelection.medicine, batch);
                                setBatchSelection({
                                  open: false,
                                  medicine: null,
                                  batches: [],
                                });
                              }}
                              sx={{ fontSize: '1.1rem' }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>

          {/* Drug Interaction Dialog */}
          <Dialog
            open={interactionDialog.open}
            onClose={() => setInteractionDialog({ open: false, interactions: [] })}
            maxWidth="md"
          >
            <DialogTitle sx={{ fontSize: '1.5rem' }}>
              <Warning color="warning" sx={{ mr: 1, fontSize: 28 }} />
              Drug Interaction Warning
            </DialogTitle>
            <DialogContent>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
                The following drug interactions were detected:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {interactionDialog.interactions.map((interaction, index) => (
                  <li key={index}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>{interaction.medicines.join(" + ")}</strong>:{" "}
                      {interaction.description}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Alert severity="warning" sx={{ 
                mt: 2,
                fontSize: '1.1rem',
                '& .MuiAlert-message': { fontSize: '1.1rem' }
              }}>
                Please consult with a pharmacist before proceeding.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setInteractionDialog({ open: false, interactions: [] })
                }
                sx={{ fontSize: '1.1rem' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() =>
                  setInteractionDialog({ open: false, interactions: [] })
                }
                sx={{ fontSize: '1.1rem' }}
              >
                Proceed Anyway
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Box>
  );
};       